import React, { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import { graphql, useLazyLoadQuery } from 'react-relay';
import styled, { css, useTheme } from 'styled-components';
import {
  formatNZD,
  removePricesAndCharacteristics,
} from '@sparknz/journeys-common';
import {
  Box,
  ChoiceChips,
  Icon,
  RawHtml,
  Text,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { PaymentOptionsAssociatedPriceRulesQuery } from '__generated__/PaymentOptionsAssociatedPriceRulesQuery.graphql';
import { PaymentOptionsQuery } from '__generated__/PaymentOptionsQuery.graphql';
import {
  MESSAGE_ME_ONLINE_CONTENT_KEY,
  UPFRONT_ONLY_ERROR_CONTENT_KEY,
} from 'journey/productDetails/constants/authorableContents.constants';
import { UPFRONT_PAYMENT_ID } from 'journey/productDetails/constants/ifp.constants';
import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import {
  IfpEligibilityCheckSteps,
  InfoLayerStatus,
  PaymentOption,
  PlanBundleOptions,
  PlanOfferDetails,
} from 'journey/productDetails/typings';
import {
  constructIfpOptions,
  getLowestPriceOffer,
} from 'journey/productDetails/utils/ifp.utils';
import { ErrorBanner } from 'shared/components';
import Clickable from 'shared/components/Clickable';
import { useQuery, useSegment } from 'shared/hooks';
import { getProductPrice } from 'shared/utils/price.utils';
import { extractFirstValue } from 'shared/utils/query.utils';
import { useWearableDevice } from '../../hooks';
import { constructPaymentOptionsDescription } from '../../utils/constructPaymentOptionsDescription.utils';
import getPaymentOptionsHeading from './utils/getPaymentOptionsHeading';

const paymentOptionsQuery = graphql`
  query PaymentOptionsQuery($input: RelatedProductInput!) {
    relatedIfp(input: $input) {
      id
      name
      description
      promotions {
        shortDescription
      }
    }
  }
`;

const paymentOptionsAssociatedPriceRulesQuery = graphql`
  query PaymentOptionsAssociatedPriceRulesQuery(
    $input: AssociatedPriceRulesInput!
  ) {
    priceRulesData: priceRulesByTargetOfferId(input: $input) {
      priceRules {
        description
        basePrice
        basePriceExcludingTax
        effectivePrice
        effectivePriceExcludingTax
        priceFrequencyPeriod
        priceFrequencyValue
        priceLength
        priceType
        downPaymentRequired
        minimumDownPaymentAmount
        sources {
          sourceId: id
        }
      }
    }
  }
`;

const GstText = styled(Text)`
  ${({ theme: { spacing } }) => css`
    margin-left: ${spacing.s0_5};
  `}
`;

const ErrorBannerWrapper = styled(Box)`
  max-width: 360px;
`;

const StyledRawHtml = styled(RawHtml)`
  color: ${({ theme: { palette } }) => `${palette.baseColors.mawhero}`};
`;

const ChoiceChipsWrapper = styled(Box)`
  & > div {
    ${({ theme: { spacing } }) => css`
      gap: ${spacing.s0_5};
    `}
  }
`;

const getPaymentOption = (
  ifpId: string,
  ifpOptions: PaymentOption[],
  upfrontOption: PaymentOption,
) => {
  if (!ifpId) {
    return getLowestPriceOffer(ifpOptions);
  }
  if (ifpId === UPFRONT_PAYMENT_ID) {
    return upfrontOption;
  }
  return ifpOptions.find((option) =>
    ifpId.includes(String(option.priceLength)),
  );
};

const IfpDescription = ({ ifpDescription }: { ifpDescription?: string }) =>
  ifpDescription ? (
    <StyledRawHtml variant="bodySDemi" html={ifpDescription} />
  ) : null;

interface PaymentChoiceBoxGroupProps {
  ifpOptions: PaymentOption[];
}

const getSelectedPlan = (planOffers: PlanOfferDetails, planId: string) => {
  return planOffers?.find((offer) => offer.id === planId) ?? planOffers?.[0];
};

const PaymentChoiceBoxGroup = ({ ifpOptions }: PaymentChoiceBoxGroupProps) => {
  const { query, replaceQuery } = useQuery();
  const { isBusiness } = useSegment();

  const { dispatch, planOffers, ifpEligibility, selectedDevice } =
    useProductDetails();
  const { isWearable } = useWearableDevice();
  const router = useRouter();

  const ifpId = extractFirstValue(router.query.ifpId);
  const planId = extractFirstValue(router.query.planId);

  const selectedPlan = getSelectedPlan(planOffers, planId);

  const paymentPeriodDescription =
    isWearable && selectedPlan?.name
      ? `On a Spark ${removePricesAndCharacteristics(selectedPlan.name)}`
      : 'On a Spark mobile plan';

  const {
    palette: {
      baseColors: { mawheranui, lupin },
    },
  } = useTheme();

  const {
    basePrice,
    basePriceExcludingTax,
    effectivePrice,
    effectivePriceExcludingTax,
  } = selectedDevice;

  const upfrontOption = {
    id: UPFRONT_PAYMENT_ID,
    name: 'payUpfront',
    description: '',
    priceLength: 1,
    basePrice,
    basePriceExcludingTax,
    effectivePrice,
    effectivePriceExcludingTax,
  };

  const paymentOption = getPaymentOption(ifpId, ifpOptions, upfrontOption);

  const paymentOptionDescription = constructPaymentOptionsDescription(
    paymentOption,
    isBusiness,
    getProductPrice(selectedDevice, isBusiness, true),
  );

  const isPayUpfront = paymentOption?.id === UPFRONT_PAYMENT_ID;
  const ifpDescription = paymentOption?.associatedPriceRuleDescription;
  const paymentPeriod = paymentOption?.priceFrequencyPeriod;
  const isDisabledIfp =
    ifpEligibility.activeStep === IfpEligibilityCheckSteps.DisableIfp;

  const handlePaymentOptionChange = (option: PaymentOption) => {
    if (option.id === UPFRONT_PAYMENT_ID) {
      dispatch({
        type: ProductDetailsActionTypes.SetPlanBundleOption,
        planBundleOption: PlanBundleOptions.DeviceOnly,
      });
    }
    trackCustomLink(option.id);
    replaceQuery({ ...query, ifpId: option.id });
  };

  if (!ifpId || !isNaN(Number(ifpId))) {
    replaceQuery({ ...query, ifpId: paymentOption.id });
  }

  const choiceChipOptions = [
    {
      label: 'Pay upfront',
      value: upfrontOption as PaymentOption,
      isChipDisabled: !selectedDevice.soldSeparately,
    },
    ...ifpOptions.map((option) => ({
      label: option.priceLength.toString(),
      value: option,
      isChipDisabled: isDisabledIfp,
    })),
  ];

  useEffect(() => {
    dispatch({
      type: ProductDetailsActionTypes.SetPaymentOptions,
      paymentOptions: ifpOptions,
    });
  }, [dispatch, ifpOptions]);

  const paymentTerm = getPaymentOptionsHeading({
    paymentOption,
    isPayUpfront,
  });

  return (
    <LinkRegion regionId="chooseDevicePaymentOptionTab">
      <Box mb="s2">
        {!isPayUpfront ? (
          <Text variant="subheadingM">Payment term:</Text>
        ) : null}
        <Box flexDirection="row">
          <Text variant="subheadingM">{paymentTerm}</Text>
          <Clickable
            aria-label="Interest free payments information icon"
            title="Interest free payments information icon"
            type="button"
            onClick={() => {
              dispatch({
                type: ProductDetailsActionTypes.SetInfoLayer,
                infoLayer: {
                  contentKey: 'productDetails.paymentOptions',
                  status: InfoLayerStatus.RawHtml,
                  isNested: false,
                },
              });
              trackCustomLink('Information icon');
            }}
          >
            <Icon name="info_small" fill={lupin} />
          </Clickable>
        </Box>
      </Box>
      {paymentOptionDescription ? (
        <Text mb="s2" variant="bodySMedium">
          {paymentOptionDescription}
        </Text>
      ) : null}
      <Box mb={['s3', 's5']}>
        <ChoiceChipsWrapper mb="s2">
          <ChoiceChips
            options={choiceChipOptions}
            selectedValue={paymentOption}
            onChange={(option) => {
              handlePaymentOptionChange(option);
            }}
          />
        </ChoiceChipsWrapper>
      </Box>
      {isDisabledIfp ? (
        <ErrorBannerWrapper mb="s4">
          <ErrorBanner
            errorContentKey={UPFRONT_ONLY_ERROR_CONTENT_KEY}
            linkContentKey={MESSAGE_ME_ONLINE_CONTENT_KEY}
          />
        </ErrorBannerWrapper>
      ) : null}

      <Box
        data-testid="payment-option-price"
        mb={paymentPeriod ? 's1' : 's5'}
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="baseline"
      >
        <Text variant="headingM">
          {`${formatNZD(
            getProductPrice(
              isPayUpfront ? selectedDevice : paymentOption,
              isBusiness,
              isPayUpfront || !paymentOption?.effectivePrice,
            ),
          )}`}
        </Text>
        <Text variant="bodySMedium">
          {paymentPeriod ? `/${paymentPeriod.toLowerCase()}` : null}
        </Text>
        {isBusiness ? (
          <GstText variant="bodySMedium" color={mawheranui}>
            (excl. GST)
          </GstText>
        ) : null}
      </Box>
      {paymentPeriod ? (
        <Text mb="s3" variant="bodySDemi" color={mawheranui}>
          {paymentPeriodDescription}
        </Text>
      ) : null}
      <Box mb={['s3', 's4', 's5']}>
        <IfpDescription ifpDescription={ifpDescription} />
      </Box>
    </LinkRegion>
  );
};

interface PaymentOptionsInternalProps {
  deviceId: string;
}

const PaymentOptionsInternal = ({ deviceId }: PaymentOptionsInternalProps) => {
  const { query, replaceQuery } = useQuery();

  const ifpId = extractFirstValue(query.ifpId);
  const planId = extractFirstValue(query.planId);
  const groupId = extractFirstValue(query.groupId);

  const { channel, isBusiness } = useSegment();

  const { selectedDevice } = useProductDetails();

  const { relatedIfp } = useLazyLoadQuery<PaymentOptionsQuery>(
    paymentOptionsQuery,
    {
      input: {
        bundles: [{ id: 'mobile', items: [{ id: deviceId }] }],
        channel,
      },
    },
  );

  useEffect(() => {
    if (isEmpty(relatedIfp) && ifpId !== UPFRONT_PAYMENT_ID) {
      replaceQuery({ ...query, ifpId: UPFRONT_PAYMENT_ID });
    }
  }, [relatedIfp]);

  const { priceRulesData } =
    useLazyLoadQuery<PaymentOptionsAssociatedPriceRulesQuery>(
      paymentOptionsAssociatedPriceRulesQuery,
      {
        input: { targetOfferId: selectedDevice.id, channel, groupId },
      },
    );

  const ifpOptionsWithPrice = useMemo(
    () =>
      constructIfpOptions(
        priceRulesData.priceRules,
        selectedDevice.associatedPrices,
        relatedIfp,
        planId,
      ),
    [
      priceRulesData.priceRules,
      planId,
      relatedIfp,
      selectedDevice.associatedPrices,
    ],
  );

  if (isEmpty(relatedIfp)) {
    return (
      <Text variant="headingM" mb="s5">
        {`${formatNZD(getProductPrice(selectedDevice, isBusiness, true))}`}
      </Text>
    );
  }

  return <PaymentChoiceBoxGroup ifpOptions={ifpOptionsWithPrice} />;
};

const PaymentOptions = () => {
  const router = useRouter();
  const deviceId = extractFirstValue(router.query.offerId);
  const { selectedDevice } = useProductDetails();
  const { isBusiness } = useSegment();
  const {
    palette: {
      baseColors: { mawheranui },
    },
  } = useTheme();

  if (!deviceId) {
    return null;
  }

  if (!selectedDevice?.isDeferrable) {
    return (
      <Box flexDirection="row" alignItems="baseline">
        <Text variant="headingM" mb="s5">
          {`${formatNZD(getProductPrice(selectedDevice, isBusiness, true))}`}
        </Text>
        {isBusiness ? (
          <GstText variant="bodySMedium" color={mawheranui}>
            (excl. GST)
          </GstText>
        ) : null}
      </Box>
    );
  }

  return <PaymentOptionsInternal deviceId={deviceId} />;
};

export default PaymentOptions;
